import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Youtube, Play } from 'lucide-react';
import axios from 'axios';
import YouTube from 'react-youtube';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourcesPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/youtube/videos`);
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 0,
    },
  };

  const onPlayerError = (event) => {
    console.error('YouTube player error:', event);
  };

  return (
    <div data-testid="resources-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-6">
              <Youtube className="text-accent mr-3" size={48} />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold" data-testid="resources-hero-title">
                The Servant CEO
              </h1>
            </div>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
              Practical insights, strategies, and inspiration for building customer-first organizations. 
              New videos published regularly.
            </p>
            
            {/* Channel Trailer Video */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="glass-card rounded-2xl overflow-hidden">
                <div className="aspect-video">
                  <YouTube
                    videoId="RoADUzbXyU0"
                    opts={opts}
                    onError={onPlayerError}
                    className="w-full h-full"
                    data-testid="channel-trailer-video"
                  />
                </div>
              </div>
            </div>
            
            <a
              href="https://youtube.com/@TheServantCEO"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="youtube-subscribe-button"
              className="inline-flex items-center justify-center bg-[#FF0000] text-white hover:bg-[#CC0000] rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
            >
              <Youtube className="mr-2" size={24} />
              Subscribe on YouTube
            </a>
          </motion.div>
        </div>
      </section>

      {/* Featured Video */}
      {selectedVideo && (
        <section className="section-padding bg-secondary/30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="glass-card rounded-2xl overflow-hidden">
                <div className="aspect-video">
                  <YouTube
                    videoId={selectedVideo.video_id}
                    opts={opts}
                    onError={onPlayerError}
                    className="w-full h-full"
                    data-testid="featured-video-player"
                  />
                </div>
                <div className="p-8">
                  <h2 className="text-2xl font-bold mb-4" data-testid="featured-video-title">
                    {selectedVideo.title}
                  </h2>
                  <p className="text-muted-foreground">{selectedVideo.description}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Video Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="latest-videos-title">
              Latest Videos
            </h2>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
              <p className="mt-4 text-muted-foreground">Loading videos...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, index) => (
                <motion.div
                  key={video.video_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`video-card-${index}`}
                  onClick={() => setSelectedVideo(video)}
                  className="glass-card rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-2 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative aspect-video bg-secondary">
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center">
                        <Play className="text-accent-foreground" size={32} />
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold mb-2 line-clamp-2">{video.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{video.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-accent/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="cta-title">
              Never Miss an Update
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Subscribe to The Servant CEO YouTube channel for weekly insights on customer-first leadership.
            </p>
            <a
              href="https://youtube.com/@TheServantCEO"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-subscribe-button"
              className="inline-flex items-center justify-center bg-[#FF0000] text-white hover:bg-[#CC0000] rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
            >
              <Youtube className="mr-2" size={24} />
              Subscribe Now
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;